import React, { useEffect, useState } from 'react';
import { Box, Button } from '@heycater/design-system';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';

import {
  AnsweringQualificationState,
  QUALIFICATION_STATUS,
} from 'qualification/context/model';
import { QuestionId } from 'qualification/schema/model';
import findIndexOnCollection from 'shared/helpers/collections';
import CardSelectItem, {
  CardSelectOption,
} from 'system/components/CardSelect/CardSelectItem';

const MIN_CARD_WIDTH = '270px';

interface Props<T> {
  questionId: QuestionId;
  onClick: (item?: T) => void;
  selected?: T | T[] | undefined;
  options: CardSelectOption<T>[];
  multi?: boolean;
  orientation?: 'vertical' | 'horizontal';
  displayOptions: number;
  state: AnsweringQualificationState;
}

function CardSelect<T extends string>({
  questionId,
  onClick,
  selected,
  options,
  multi = false,
  orientation = 'vertical',
  displayOptions,
  state,
}: Props<T>) {
  const answer = state.qualification.answers[questionId];
  const answerIndex = findIndexOnCollection(answer, options);
  const mustShowAll = answerIndex > displayOptions - 1;

  const [showAll, setShowAll] = useState(mustShowAll ? true : false);

  useEffect(() => {
    if (!showAll && mustShowAll) {
      setShowAll(true);
    }
  }, [mustShowAll]);

  const router = useRouter();

  useEffect(() => {
    setShowAll(mustShowAll ? true : false);
  }, [router]);

  const { t } = useTranslation('qualification');
  const showAllButton = options.length > displayOptions;

  if (state.status !== QUALIFICATION_STATUS.answering) {
    return null;
  }

  return (
    <Box textAlign="center" width="100%">
      <StyledCardSelect className="card-select" id={questionId}>
        {(showAllButton ? options.slice(0, displayOptions) : options).map(
          (option, index) => (
            <CardSelectItemGridItemLayout
              key={`${option.value}::${index}`}
              className="card-select-item"
              data-value={option.value}
              $orientation={orientation}
            >
              <CardSelectItem
                option={option}
                onClick={onClick}
                selected={selected}
                multi={multi}
                orientation={orientation}
              />
            </CardSelectItemGridItemLayout>
          )
        )}
        {showAll &&
          showAllButton &&
          options?.slice(displayOptions).map((option, index) => (
            <CardSelectItemGridItemLayout
              key={`${option.value}::${index}`}
              className="card-select-item"
              data-value={option.value}
              $orientation={orientation}
            >
              <CardSelectItem
                option={option}
                onClick={onClick}
                selected={selected}
                multi={multi}
                orientation={orientation}
              />
            </CardSelectItemGridItemLayout>
          ))}
      </StyledCardSelect>
      {showAllButton && (
        <Box mb={5} mt={4}>
          <Button
            disabled={mustShowAll}
            outlined
            onClick={() => setShowAll(!showAll)}
            variant="secondary"
          >
            <Box py={0.5}>
              {showAll
                ? t('request.form.showLess')
                : t('request.form.showMore')}
            </Box>
          </Button>
        </Box>
      )}
    </Box>
  );
}

const CardSelectItemGridItemLayout = styled.div<{
  $orientation: 'vertical' | 'horizontal';
}>`
  padding: ${(props) => props.theme.spacing(1)}px;

  ${(props) =>
    props.$orientation === 'horizontal'
      ? css`
          flex-shrink: 0;
          width: max(${MIN_CARD_WIDTH}, 33.332%);
          ${(props) => props.theme.breakpoints.down('sm')} {
            width: max(${MIN_CARD_WIDTH}, 50%);
          } ;
        `
      : css`
          width: 25%;
          ${(props) => props.theme.breakpoints.down('sm')} {
            width: 33.332%;
          } ;
        `}

  ${(props) => props.theme.breakpoints.down('xs')} {
    width: 100%;
  }
`;

const StyledCardSelect = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  margin-bottom: ${(props) => props.theme.spacing(2)}px;

  ${(props) => props.theme.breakpoints.down('xs')} {
    flex-direction: column;
  }
`;

export default CardSelect;
